import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UiKitModule } from '@serviceplan/ui-kit';
import { BackgroundProcessService, ClientEnvironment, ClientNotificationHandlerService, ENVIRONMENT, TableColumn } from '@thehubz/client/core';
import { AssetHistory, AssetHistoryApiService, AssetHistoryResultResponse, ExportExcelRequest, ExportsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { Subscription, tap } from 'rxjs';
import { AssetCommonService } from '../../../services/asset-common.service';
import { TableColumnData } from './table-column-data';

@Component({
  selector: 'thehubz-asset-download-history',
  templateUrl: './asset-download-history.component.html',
  styleUrls: ['./asset-download-history.component.scss'],
  standalone: true,
  imports: [CommonModule, UiKitModule],
})
export class AssetDownloadHistoryComponent implements OnInit, OnDestroy {
  httpCallInProgress = false;
  downloadData: AssetHistory[] = [];
  columns!: TableColumn[];
  readonly brandingKey!: CustomClients;

  pageType = $localize`:page-type:asset download list`;

  private excelDownloadSub: Subscription | undefined;

  constructor(
    @Inject(ENVIRONMENT) private environment: ClientEnvironment,
    private router: Router,
    private assetCommonService: AssetCommonService,
    private assetHistoryService: AssetHistoryApiService,
    private exportExcelApiService: ExportsApiService,
    private backgroundProcessService: BackgroundProcessService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {
    this.brandingKey = environment.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit(): void {
    this.columns = TableColumnData.TABLE_DEFAULT_COLUMNS.filter((column) => {
      return !column.excludeClient || (column.excludeClient && !column.excludeClient.includes(this.brandingKey));
    });
    this._showLoader();
    this.fetchAssetHistory();
  }

  fetchAssetHistory() {
    const currentPath = this.router.url.split('/');
    const assetID = currentPath[currentPath.length - 2];
    const filter = { filters: { downloadedAsset: assetID } };
    this.assetHistoryService
      .assetHistoryControllerFindAllDownloadedAssets(JSON.stringify(filter))
      .pipe(
        tap(() => {
          this._showLoader(false);
        }),
      )
      .subscribe({
        next: (response: AssetHistoryResultResponse) => {
          this.assetCommonService.assetDownloadHistoryCount.next(response.count);
          if (response.result && response.result.length > 0) {
            this.downloadData = response.result;
          }
        },
        error: (err) => {
          this.clientNotificationHandlerService.errorNotification(err);
        },
      });

    this.excelDownloadSub = this.assetCommonService.assetDownloadExcel.subscribe((res) => {
      if (res && this.downloadData.length) {
        // filter is the mongo query for mongo.find()
        // filterOptions is the options for mongo.find()
        let filterOptions = {};
        if (this.brandingKey === CustomClients.SEAT) {
          filterOptions = { agency: 0, channel: 0 };
        }
        const process$ = this.exportExcelApiService.exportsControllerExportExcel({
          type: ExportExcelRequest.TypeEnum.Assethistories,
          filter: JSON.stringify(filter.filters),
          fileName: this.environment.exportDownloads?.fileName,
          filterOptions: JSON.stringify(filterOptions),
          addTime: this.environment.exportDownloads?.addTime,
        });
        this.backgroundProcessService.addExportProcess(process$, ExportExcelRequest.TypeEnum.Assethistories);
      }
    });
  }

  /**
   * Utility method to show/hide loader
   */
  private _showLoader(show = true) {
    this.httpCallInProgress = show;
  }

  ngOnDestroy(): void {
    this.excelDownloadSub && this.excelDownloadSub.unsubscribe();
  }
}
